import { Link } from 'react-router-dom';
import { format, isToday, isThisWeek } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { useDateLocale } from '@/lib/date-locale';
import type { AuditItem, ActivationItem } from '@/types/api';
import type { Locale } from 'date-fns';

interface RecentActivityProps {
  items: (AuditItem | ActivationItem)[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  isAudit: boolean;
}

function isAuditItem(item: AuditItem | ActivationItem): item is AuditItem {
  return 'event' in item && 'performedBy' in item;
}

function itemDate(item: AuditItem | ActivationItem): Date {
  return new Date(isAuditItem(item) ? item.timestamp : item.activatedAt);
}

function groupLabelKey(date: Date): string {
  if (isToday(date)) return 'overview.activity.today';
  if (isThisWeek(date, { weekStartsOn: 1 })) return 'overview.activity.thisWeek';
  return 'overview.activity.earlier';
}

function whenLabel(date: Date, locale: Locale): string {
  if (isToday(date)) return format(date, 'HH:mm');
  if (isThisWeek(date, { weekStartsOn: 1 })) return format(date, 'EEE', { locale });
  return format(date, 'd MMM', { locale });
}

interface ActivityParts {
  verb: string;
  serial: string;
  actor?: string;
}

function translatedVerb(event: string, t: TFunction): string {
  const key = `common.events.${event.toLowerCase()}`;
  const translated = t(key);
  return translated === key ? event : translated;
}

function activityParts(item: AuditItem | ActivationItem, t: TFunction): ActivityParts {
  if (isAuditItem(item)) {
    return { verb: translatedVerb(item.event, t), serial: item.serial, actor: item.performedBy };
  }
  return { verb: translatedVerb('activated', t), serial: item.serial };
}

export function RecentActivity({
  items,
  isLoading,
  isError,
  onRetry,
  isAudit,
}: RecentActivityProps) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();

  if (isError) {
    return (
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-foreground">
          {t('overview.activity.title')}
        </h2>
        <p className="mt-4 text-sm text-destructive">{t('overview.activity.error')}</p>
        <button onClick={onRetry} className="mt-2 text-sm text-primary underline">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  const groups: { labelKey: string; items: (AuditItem | ActivationItem)[] }[] = [];
  for (const item of items) {
    const labelKey = groupLabelKey(itemDate(item));
    const last = groups[groups.length - 1];
    if (last && last.labelKey === labelKey) {
      last.items.push(item);
    } else {
      groups.push({ labelKey, items: [item] });
    }
  }

  return (
    <div>
      <div className="mb-5 flex items-baseline justify-between gap-4">
        <h2 className="text-xl font-semibold tracking-tight text-foreground">
          {t('overview.activity.title')}
        </h2>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('overview.activity.empty')}</p>
      ) : (
        groups.map((group, groupIndex) => (
          <div key={group.labelKey}>
            <div
              className={`mb-1 text-[13.5px] font-semibold text-muted-foreground ${
                groupIndex === 0 ? 'mt-0' : 'mt-5'
              }`}
            >
              {t(group.labelKey)}
            </div>
            {group.items.map((item) => {
              const parts = activityParts(item, t);
              const date = itemDate(item);
              return (
                <Link
                  key={item.id}
                  to={`/licenses/${item.licenseId}`}
                  className="flex items-baseline justify-between gap-4 rounded-md px-1 py-2.5 transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="min-w-0 truncate text-[14.5px] capitalize text-muted-foreground">
                    {parts.verb}{' '}
                    <span className="font-medium normal-case text-foreground">{parts.serial}</span>
                    {parts.actor && (
                      <span className="normal-case text-faint">
                        {' '}
                        {t('overview.activity.by')} {parts.actor}
                      </span>
                    )}
                  </span>
                  <span className="whitespace-nowrap text-[13px] text-faint">
                    {whenLabel(date, dateLocale)}
                  </span>
                </Link>
              );
            })}
          </div>
        ))
      )}
      {!isAudit && !isLoading && items.length > 0 && (
        <p className="pt-3 text-sm text-faint">{t('overview.activity.auditNote')}</p>
      )}
    </div>
  );
}
